import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit2, FileText, Trash2, Filter, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import Spinner from './Spinner.js';

const MaterialMovement = ({ db }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const q = query(collection(db, "dispatches"), orderBy("updatedAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mocList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMocs(mocList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => {
      const matchesSearch = 
        moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesWarehouse = filterWarehouse === 'All' || moc.warehouse === filterWarehouse;

      return matchesSearch && matchesWarehouse;
    });
  }, [mocs, searchTerm, filterWarehouse]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this MOC draft?")) {
      try {
        await deleteDoc(doc(db, "dispatches", id));
      } catch (error) {
        alert("Error deleting MOC: " + error.message);
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'packed_ready':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> PACKING DONE
            </span>;
        case 'invoice_pending':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> DC PREPARED
            </span>;
        case 'invoice_ready':
        case 'dc_confirmed':
        case 'qc_pending':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> INVOICE READY
            </span>;
        case 'qc_passed':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> QC PASSED
            </span>;
        case 'moc_approved':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle size={12}/> MOC APPROVED
            </span>;
        case 'moc_pending_finance':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                <Clock size={12}/> WAITING FINANCE
            </span>;
        default:
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#f3f4f6', color: '#64748b' }}>
                <Clock size={12}/> {status?.replace(/_/g, ' ') || 'DRAFT'}
            </span>;
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Material Out Checklists</h2>
          <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>Manage shipment drafts and approvals.</p>
        </div>
        <button 
          onClick={() => navigate('/ops/moc/new')} 
          style={createBtnStyle}
        >
          <Plus size={18} /> {isMobile ? 'New' : 'Create MOC'}
        </button>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Customer or MOC No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Filter size={18} color="#64748b" />
          <select 
            value={filterWarehouse} 
            onChange={(e) => setFilterWarehouse(e.target.value)}
            style={selectInputStyle}
          >
            <option value="All">All Warehouses</option>
            <option value="Jaipur">Jaipur</option>
            <option value="Ahmedabad">Ahmedabad</option>
            <option value="Bhiwandi">Bhiwandi</option>
            <option value="New Delhi">New Delhi</option>
            <option value="Hyderabad">Hyderabad</option>
          </select>
        </div>
      </div>

      {/* TABLE / LIST */}
      <div style={containerStyle}>
        {filteredMocs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <FileText size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <p>No Material Out Checklists found.</p>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>MOC No.</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => (
                <tr 
                  key={moc.id} 
                  style={trStyle} 
                  onClick={() => navigate(`/ops/moc/preview/${moc.id}`)}
                >
                  <td style={tdStyle}>{moc.mocDate}</td>
                  <td style={{ ...tdStyle, fontWeight: 'bold', color: '#2563eb' }}>{moc.mocNo}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600' }}>{moc.customerName}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{moc.materialCode}</div>
                  </td>
                  <td style={tdStyle}>{moc.typeOfMOC}</td>
                  <td style={tdStyle}>{moc.warehouse}</td>
                  <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      {moc.dispatchStatus !== 'moc_pending_finance' && (
                        <button style={actionBtnStyle} title="Edit"><Edit2 size={16}/></button>
                      )}
                      <button 
                        onClick={(e) => handleDelete(e, moc.id)} 
                        style={{ ...actionBtnStyle, color: '#ef4444' }} 
                        title="Delete"
                      >
                        <Trash2 size={16}/>
                      </button>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// --- STYLES ---
const containerStyle = {
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden'
};

const filterBarStyle = {
  display: 'flex',
  gap: '15px',
  marginBottom: '20px',
  flexWrap: 'wrap'
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 10px 10px 40px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  outline: 'none'
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#94a3b8'
};

const selectInputStyle = {
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  backgroundColor: 'white',
  outline: 'none',
  minWidth: '160px'
};

const createBtnStyle = {
  backgroundColor: '#1e293b',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const theadStyle = {
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0'
};

const thStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const trStyle = {
  borderBottom: '1px solid #f1f5f9',
  cursor: 'pointer',
  transition: 'background-color 0.2s'
};

const tdStyle = {
  padding: '15px',
  fontSize: '14px',
  color: '#1e293b'
};

const actionBtnStyle = {
  background: 'none',
  border: 'none',
  padding: '5px',
  color: '#64748b',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};

export default MaterialMovement;
